import { useCallback, useEffect, useRef, useState } from 'react';

const BACKOFF_MULTIPLIERS = [1, 2, 4, 10];

const MAX_CONSECUTIVE_ERRORS = 5;

const TICK_TIMEOUT_MS = 10000;

export interface UsePollingResult<TData> {
  data: TData | null;
  error: string | null;
  isPaused: boolean;
  retry: () => void;
}

function backoffDelay(errorCount: number, intervalMs: number): number {
  const index = Math.min(errorCount, BACKOFF_MULTIPLIERS.length) - 1;
  return intervalMs * BACKOFF_MULTIPLIERS[index];
}

export function usePolling<TData>(
  fetcher: (signal: AbortSignal) => Promise<TData>,
  enabled: boolean,
  intervalMs: number
): UsePollingResult<TData> {
  const [data, setData] = useState<TData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [restartToken, setRestartToken] = useState(0);
  const fetcherRef = useRef(fetcher);
  const immediateRef = useRef(false);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    let stopped = false;
    let inFlight = false;
    let errorCount = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let activeController: AbortController | null = null;

    const clearTimer = () => {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = (delayMs: number) => {
      if (cancelled || stopped) {
        return;
      }
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        void tick();
      }, delayMs);
    };

    const tick = async () => {
      if (cancelled || stopped || inFlight) {
        return;
      }
      if (document.visibilityState === 'hidden') {
        return;
      }
      inFlight = true;
      const controller = new AbortController();
      activeController = controller;
      const timeoutHandle = setTimeout(() => {
        controller.abort();
      }, TICK_TIMEOUT_MS);
      try {
        const result = await fetcherRef.current(controller.signal);
        clearTimeout(timeoutHandle);
        inFlight = false;
        if (cancelled) {
          return;
        }
        errorCount = 0;
        setData(result);
        setError(null);
        schedule(intervalMs);
      } catch (failure) {
        clearTimeout(timeoutHandle);
        inFlight = false;
        if (cancelled) {
          return;
        }
        errorCount += 1;
        setError((failure as Error)?.message || 'Failed to refresh the data.');
        if (errorCount >= MAX_CONSECUTIVE_ERRORS) {
          stopped = true;
          setIsPaused(true);
          return;
        }
        schedule(backoffDelay(errorCount, intervalMs));
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearTimer();
        return;
      }
      if (!cancelled && !stopped && !inFlight && timer == null) {
        void tick();
      }
    };

    setIsPaused(false);
    schedule(immediateRef.current ? 0 : intervalMs);
    immediateRef.current = false;
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimer();
      activeController?.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, intervalMs, restartToken]);

  const retry = useCallback(() => {
    immediateRef.current = true;
    setIsPaused(false);
    setError(null);
    setRestartToken(x => x + 1);
  }, []);

  return { data, error, isPaused, retry };
}
